import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ImageCropperModal from '../ImageCropperModal';
import { useCustomDialog } from '../../context/DialogContext';
import toast from 'react-hot-toast';

const BASE = 'https://menu.searchisend.in';

const RestaurantSettings = ({ token, onLogoUpdate }) => {
    const { confirm: customConfirm } = useCustomDialog();
    const [settings, setSettings] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [logoUploading, setLogoUploading] = useState(false);
    const [cropImageSrc, setCropImageSrc] = useState(null);
    const [passwordData, setPasswordData] = useState({ current_password: '', new_password: '' });
    const [passwordSaving, setPasswordSaving] = useState(false);

    useEffect(() => {
        fetchSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchSettings = async () => {
        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_settings.php?action=fetch', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                // Ensure null values are safely empty strings for inputs
                const cleanedSettings = {};
                Object.keys(data.settings).forEach(k => {
                    cleanedSettings[k] = data.settings[k] === null ? '' : data.settings[k];
                });
                setSettings(cleanedSettings);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        let finalValue = type === 'checkbox' ? (checked ? 1 : 0) : value;
        
        // Strict phone validation
        if (name === 'owner_phone' || name === 'restaurant_phone') {
            finalValue = finalValue.replace(/\D/g, '').slice(0, 10);
        }

        setSettings(prev => ({
            ...prev,
            [name]: finalValue
        }));
    };

    const applyToAllDays = () => {
        const sourceOpen = settings['open_time_sun'];
        const sourceClose = settings['close_time_sun'];
        const sourceClosed = settings['is_closed_sun'];
        const days = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

        const newSettings = { ...settings };
        days.forEach(day => {
            newSettings[`open_time_${day}`] = sourceOpen;
            newSettings[`close_time_${day}`] = sourceClose;
            newSettings[`is_closed_${day}`] = sourceClosed;
        });
        setSettings(newSettings);
        toast.success('Applied Sunday limits to all days! Don\'t forget to save.');
    };

    const handleSave = async (e) => {
        e.preventDefault();

        // Strict phone validation: 10 digits
        const phoneRegex = /^[0-9]{10}$/;
        if (settings.owner_phone && !phoneRegex.test(settings.owner_phone)) {
            toast.error('Owner Phone must be exactly 10 digits.');
            return;
        }

        // Validation: Ensure at least one order type is enabled
        const dineIn = Number(settings.is_dine_in_enabled) === 1;
        const takeaway = Number(settings.is_takeaway_enabled) === 1;
        const delivery = Number(settings.is_delivery_enabled) === 1;

        if (!dineIn && !takeaway && !delivery) {
            toast.error('At least one order type (Dine-in, Takeaway, or Delivery) must be enabled.');
            return;
        }

        setSaving(true);
        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_settings.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ action: 'update', ...settings })
            });
            const data = await res.json();
            if (data.success) {
                toast.success('Settings updated successfully!');
            } else {
                toast.error(data.message || 'Failed to update settings');
            }
        } catch (err) {
            toast.error('Failed to update settings.');
        } finally {
            setSaving(false);
        }
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        setPasswordSaving(true);
        
        const fd = new FormData();
        fd.append('action', 'update_password');
        fd.append('current_password', passwordData.current_password);
        fd.append('new_password', passwordData.new_password);
        
        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_settings.php', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: fd
            });
            const data = await res.json();
            if (data.success) {
                toast.success(data.message);
                setPasswordData({ current_password: '', new_password: '' });
            } else {
                toast.error(data.message || 'Failed to update password');
            }
        } catch (err) {
            toast.error('Error updating password');
        } finally {
            setPasswordSaving(false);
        }
    };

    const handleLogoFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setCropImageSrc(URL.createObjectURL(file));
            e.target.value = '';
        }
    };

    const handleCropComplete = async (croppedBlob) => {
        setCropImageSrc(null);
        setLogoUploading(true);
        const formData = new FormData();
        const file = new File([croppedBlob], 'logo.jpg', { type: 'image/jpeg' });
        formData.append('logo_image', file);
        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_logo.php?action=upload`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: formData
            });
            const data = await res.json();
            if (data.success) {
                toast.success('Logo updated successfully');
                fetchSettings();
                if (onLogoUpdate) onLogoUpdate(data.logo_url);
            } else {
                toast.error(data.message || 'Upload failed.');
            }
        } catch { toast.error('Error uploading logo.'); }
        finally { setLogoUploading(false); }
    };

    const handleLogoDelete = async () => {
        if (!await customConfirm('Permanently delete your logo?', 'Delete Logo', 'danger')) return;
        setLogoUploading(true);
        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_logo.php?action=delete`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                toast.success('Logo deleted.');
                fetchSettings();
                if (onLogoUpdate) onLogoUpdate(null);
            } else {
                toast.error(data.message);
            }
        } catch { toast.error('Error deleting logo.'); }
        finally { setLogoUploading(false); }
    };

    const inputStyle = { width: '100%', padding: '12px', marginBottom: '20px', border: '1px solid var(--border-color)', borderRadius: '5px', boxSizing: 'border-box' };
    const labelStyle = { display: 'block', marginBottom: '8px', fontWeight: '500' };

    if (loading) return <div className="admin-card">Loading Settings...</div>;

    return (
        <div className="admin-card">
            <Link to="/manage_v2_secure_auth/" className="sticky-back-btn">
                <i className="fas fa-arrow-left"></i> Back to Dashboard
            </Link>
            <h1 style={{ marginTop: '0' }}>Restaurant Settings</h1>

            {/* ── Logo Section ── */}
            <div style={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '20px', marginBottom: '20px' }}>
                <h4 style={{ marginTop: '0', color: 'var(--text-color-headings)', fontSize: '1.2em', marginBottom: '20px' }}>Restaurant Logo</h4>
                {settings.logo_url ? (
                    <div style={{ marginBottom: '15px' }}>
                        <p style={{ marginBottom: '8px' }}>Current Logo:</p>
                        <img src={settings.logo_url} alt="Logo" style={{ maxWidth: '100px', height: 'auto', borderRadius: '8px', border: '1px solid var(--border-color)', marginBottom: '12px', display: 'block' }} />
                        <button type="button" onClick={handleLogoDelete} style={{ backgroundColor: 'var(--red-accent)', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '50px', fontWeight: '600', cursor: 'pointer' }}>Delete Logo</button>
                    </div>
                ) : (
                    <p style={{ color: 'var(--text-color-secondary)', marginBottom: '12px' }}>No logo uploaded yet.</p>
                )}
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <input type="file" accept="image/*" onChange={handleLogoFileChange} style={{ flex: 1, minWidth: '0', width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '5px', boxSizing: 'border-box' }} />
                    {logoUploading && <span style={{ fontWeight: '600', color: 'var(--primary-orange)' }}>Uploading...</span>}
                </div>
            </div>

            <div style={{ backgroundColor: 'var(--header-actions-bg)', padding: '20px', borderRadius: '15px', marginBottom: '30px', border: '2px dashed var(--primary-orange)', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                <div>
                    <h4 style={{ margin: '0 0 5px 0', color: 'var(--primary-orange)' }}>Your Public Menu is LIVE</h4>
                    <p style={{ margin: 0, fontWeight: '600', fontSize: '1.1rem', wordBreak: 'break-all' }}>{`${BASE}/${settings.menu_slug}`}</p>
                </div>
                <a href={`${BASE}/${settings.menu_slug}`} target="_blank" rel="noopener noreferrer" style={{ backgroundColor: 'var(--primary-orange)', color: 'white', padding: '10px 20px', borderRadius: '50px', textDecoration: 'none', fontWeight: '700', fontSize: '0.9rem' }}>
                    <i className="fas fa-external-link-alt" style={{ marginRight: '8px' }} /> View Live Menu
                </a>
            </div>

            <form onSubmit={handleSave}>
                <div style={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '20px', marginBottom: '20px' }}>
                    <h4 style={{ marginTop: '0', color: 'var(--text-color-headings)', fontSize: '1.2em', marginBottom: '20px' }}>User Profile</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                        <div><label style={labelStyle}>Owner Name</label><input type="text" name="owner_name" value={settings.owner_name} onChange={handleChange} style={inputStyle} /></div>
                        <div><label style={labelStyle}>Username</label><input type="text" name="username" value={settings.username} onChange={handleChange} readOnly style={{ ...inputStyle, backgroundColor: '#f0f0f0', cursor: 'not-allowed' }} title="Username cannot be changed directly" /></div>
                        <div><label style={labelStyle}>Owner Phone</label><input type="text" name="owner_phone" value={settings.owner_phone} onChange={handleChange} style={inputStyle} placeholder="10-digit number" /></div>
                        <div><label style={labelStyle}>Restaurant Phone</label><input type="text" name="restaurant_phone" value={settings.restaurant_phone} onChange={handleChange} style={inputStyle} placeholder="10-digit number" /></div>
                        <div><label style={labelStyle}>Receipt Email</label><input type="email" name="email" required value={settings.email} onChange={handleChange} style={inputStyle} /></div>
                        <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Restaurant Address</label><textarea name="restaurant_address" value={settings.restaurant_address} onChange={handleChange} style={{ ...inputStyle, resize: 'vertical', minHeight: '60px' }} /></div>
                        <div><label style={labelStyle}>City</label><input type="text" name="city" value={settings.city} onChange={handleChange} style={inputStyle} /></div>
                        <div><label style={labelStyle}>State</label><input type="text" name="state" value={settings.state} onChange={handleChange} style={inputStyle} /></div>
                    </div>
                </div>

                <div style={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '20px', marginBottom: '20px' }}>
                    <h4 style={{ marginTop: '0', color: 'var(--text-color-headings)', fontSize: '1.2em', marginBottom: '20px' }}>Restaurant Details & Theme</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                        <div>
                            <label style={labelStyle}>Restaurant Name</label>
                            <input type="text" name="restaurant_name" required value={settings.restaurant_name} onChange={handleChange} style={inputStyle} />
                        </div>
                        <div>
                            <label style={labelStyle}>Menu Slug (Public URL)</label>
                            <input type="text" name="menu_slug" required value={settings.menu_slug} onChange={handleChange} style={inputStyle} />
                        </div>
                        <div>
                            <label style={labelStyle}>Primary Theme Color</label>
                            <div style={{ display: 'flex', gap: '15px', alignItems: 'center', marginBottom: '20px' }}>
                                <input type="color" name="theme_color" value={settings.theme_color} onChange={handleChange} style={{ height: '50px', width: '50px', padding: '5px', border: '1px solid var(--border-color)', borderRadius: '50%', cursor: 'pointer', appearance: 'none', overflow: 'hidden' }} />
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    {['#EF4444', '#3B82F6', '#22C55E', '#8B5CF6', '#F97316'].map(c => (
                                        <div
                                            key={c}
                                            onClick={() => setSettings(s => ({ ...s, theme_color: c }))}
                                            style={{ width: '28px', height: '28px', borderRadius: '50%', backgroundColor: c, cursor: 'pointer', border: settings.theme_color === c ? '3px solid #000' : '1px solid var(--border-color)', boxSizing: 'border-box' }}
                                            title={c}
                                        />
                                    ))}
                                </div>
                                <button type="button" onClick={() => setSettings(s => ({ ...s, theme_color: '#F97316' }))} style={{ backgroundColor: 'var(--header-actions-bg)', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '5px 12px', cursor: 'pointer', fontSize: '0.85em', fontWeight: '600' }}>
                                    Restore Default
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div style={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '20px', marginBottom: '20px' }}>
                    <h4 style={{ marginTop: '0', color: 'var(--text-color-headings)', fontSize: '1.2em', marginBottom: '20px' }}>Announcement Bar</h4>
                    <p style={{ fontSize: '0.9em', color: 'var(--text-color-secondary)', marginTop: '-15px', marginBottom: '15px' }}>This scrolling text appears at the very top of your menu. Leave blank to hide.</p>
                    <label style={labelStyle}>Scrolling Text:</label>
                    <input type="text" name="announcement_text" value={settings.announcement_text || ''} onChange={handleChange} style={inputStyle} placeholder="e.g. 50% Off on all pizzas this weekend!" />
                </div>

                <div style={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '20px', marginBottom: '20px' }}>
                    <h4 style={{ marginTop: '0', color: 'var(--text-color-headings)', fontSize: '1.2em', marginBottom: '20px' }}>Order Types & Delivery</h4>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginBottom: '20px' }}>
                        <div><label style={labelStyle}>Table Count (Dine-in):</label><input type="number" name="table_count" required value={settings.table_count} onChange={handleChange} style={inputStyle} /></div>
                    </div>

                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}><input type="checkbox" name="veg_filter_default_on" checked={Number(settings.veg_filter_default_on) === 1} onChange={handleChange} style={{ width: '20px', height: '20px' }} /> Enable 'Veg Only' Filter by Default</label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}><input type="checkbox" name="is_dine_in_enabled" checked={Number(settings.is_dine_in_enabled) === 1} onChange={handleChange} style={{ width: '20px', height: '20px' }} /> Enable Dine-in Orders</label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}><input type="checkbox" name="is_takeaway_enabled" checked={Number(settings.is_takeaway_enabled) === 1 || settings.is_takeaway_enabled === undefined} onChange={handleChange} style={{ width: '20px', height: '20px' }} /> Enable Takeaway Orders</label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}><input type="checkbox" name="is_delivery_enabled" checked={Number(settings.is_delivery_enabled) === 1} onChange={handleChange} style={{ width: '20px', height: '20px' }} /> Enable Home Delivery Orders</label>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                        <div><label style={labelStyle}>Delivery Charge (₹):</label><input type="number" step="0.01" name="delivery_charge" value={settings.delivery_charge} onChange={handleChange} style={inputStyle} /></div>
                        <div><label style={labelStyle}>Free Delivery Over (₹):</label><input type="number" step="0.01" name="free_delivery_over" value={settings.free_delivery_over} onChange={handleChange} style={inputStyle} /></div>
                    </div>
                </div>

                <div style={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '20px', marginBottom: '20px' }}>
                    <h4 style={{ marginTop: '0', color: 'var(--text-color-headings)', fontSize: '1.2em', marginBottom: '20px' }}>Checkout Options</h4>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                        <input type="checkbox" name="is_manual_payment_enabled" checked={Number(settings.is_manual_payment_enabled) === 1} onChange={handleChange} style={{ width: '20px', height: '20px' }} />
                        Require Payment Confirmation before Kitchen (Awaiting Payment status)
                    </label>

                    {/* WhatsApp Ordering Toggle hidden as per plan */}

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginTop: '15px' }}>
                        <div>
                            <label style={labelStyle}>Dine-in Advance Required (%):</label>
                            <input type="number" min="0" max="100" name="advance_dinein_percent" value={settings.advance_dinein_percent || 0} onChange={handleChange} style={inputStyle} />
                            <small style={{ color: 'var(--text-color-secondary)' }}>0 for full payment/no advance.</small>
                        </div>
                        <div>
                            <label style={labelStyle}>Takeaway Advance Required (%):</label>
                            <input type="number" min="0" max="100" name="advance_takeaway_percent" value={settings.advance_takeaway_percent || 0} onChange={handleChange} style={inputStyle} />
                        </div>
                        <div>
                            <label style={labelStyle}>Delivery Advance Required (%):</label>
                            <input type="number" min="0" max="100" name="advance_delivery_percent" value={settings.advance_delivery_percent || 0} onChange={handleChange} style={inputStyle} />
                        </div>
                    </div>
                </div>

                <div style={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '20px', marginBottom: '20px' }}>
                    <h4 style={{ marginTop: '0', color: 'var(--text-color-headings)', fontSize: '1.2em', marginBottom: '20px' }}>Restaurant Timings</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                        {['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'].map(day => (
                            <fieldset key={day} style={{ border: '1px solid var(--border-color)', padding: '15px', borderRadius: '5px' }}>
                                <legend style={{ fontWeight: '600', padding: '0 5px' }}>{day.toUpperCase()}</legend>
                                <label style={{ fontSize: '0.9em', marginBottom: '5px', display: 'block' }}>Open:</label>
                                <input type="time" name={`open_time_${day}`} value={settings[`open_time_${day}`]} onChange={handleChange} style={{ width: '100%', padding: '8px', marginBottom: '10px' }} />
                                <label style={{ fontSize: '0.9em', marginBottom: '5px', display: 'block' }}>Close:</label>
                                <input type="time" name={`close_time_${day}`} value={settings[`close_time_${day}`]} onChange={handleChange} style={{ width: '100%', padding: '8px', marginBottom: '10px' }} />
                                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.9em' }}><input type="checkbox" name={`is_closed_${day}`} checked={Number(settings[`is_closed_${day}`]) === 1} onChange={handleChange} /> Closed</label>
                                {day === 'sun' && (
                                    <button type="button" onClick={applyToAllDays} style={{ marginTop: '15px', width: '100%', padding: '8px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '0.9em' }}>Apply to All</button>
                                )}
                            </fieldset>
                        ))}
                    </div>
                </div>

                <div style={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '20px', marginBottom: '20px' }}>
                    <h4 style={{ marginTop: '0', color: 'var(--text-color-headings)', fontSize: '1.2em', marginBottom: '20px' }}>Social & Review Links</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                        <div><label style={labelStyle}>WhatsApp Number (with country code, e.g. 919876543210):</label><input type="text" name="social_whatsapp" value={settings.social_whatsapp || ''} onChange={handleChange} style={inputStyle} /></div>
                        <div><label style={labelStyle}>Instagram Link:</label><input type="text" name="social_instagram" value={settings.social_instagram || ''} onChange={handleChange} style={inputStyle} /></div>
                        <div><label style={labelStyle}>Facebook Link:</label><input type="text" name="social_facebook" value={settings.social_facebook || ''} onChange={handleChange} style={inputStyle} /></div>
                        <div><label style={labelStyle}>YouTube Link:</label><input type="text" name="social_youtube" value={settings.social_youtube || ''} onChange={handleChange} style={inputStyle} /></div>
                        <div><label style={labelStyle}>Google Review / Any Review Link:</label><input type="text" name="review_link" value={settings.review_link || ''} onChange={handleChange} style={inputStyle} /></div>
                    </div>
                </div>

                {/* UPI Payment */}
                <div style={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '20px', marginBottom: '20px' }}>
                    <h4 style={{ marginTop: '0', color: 'var(--text-color-headings)', fontSize: '1.2em', marginBottom: '20px' }}>UPI Payment Details</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                        <div><label style={labelStyle}>UPI ID:</label><input type="text" name="upi_id" value={settings.upi_id || ''} onChange={handleChange} style={inputStyle} placeholder="e.g. name@upi" /></div>
                        <div><label style={labelStyle}>UPI Payee Name:</label><input type="text" name="upi_payee_name" value={settings.upi_payee_name || ''} onChange={handleChange} style={inputStyle} /></div>
                    </div>
                </div>

                {/* Notification Coupon */}
                <div style={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '20px', marginBottom: '20px' }}>
                    <h4 style={{ marginTop: '0', color: 'var(--text-color-headings)', fontSize: '1.2em', marginBottom: '20px' }}>Notification Welcome Coupon</h4>
                    <p style={{ fontSize: '0.9em', color: 'var(--text-color-secondary)', marginTop: 0 }}>Set a coupon code that is revealed to users after they subscribe to push notifications. The coupon must already be created in "Manage Coupons".</p>
                    <div style={{ maxWidth: '400px' }}>
                        <label style={labelStyle}>Welcome Coupon Code:</label>
                        <input type="text" name="notification_coupon_code" value={settings.notification_coupon_code || ''} onChange={handleChange} style={inputStyle} placeholder="e.g. WELCOME10" />
                    </div>
                </div>

                <div style={{ marginTop: '30px' }}>
                    <button type="submit" disabled={saving} style={{ backgroundColor: 'var(--primary-orange)', color: 'white', border: 'none', padding: '12px 30px', borderRadius: '50px', fontWeight: '600', cursor: 'pointer', fontSize: '1.1em' }}>
                        {saving ? 'Saving...' : 'Save All Settings'}
                    </button>
                </div>
            </form>

            <form onSubmit={handlePasswordChange} style={{ marginTop: '30px' }}>
                <div style={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '20px', marginBottom: '20px' }}>
                    <h4 style={{ marginTop: '0', color: 'var(--text-color-headings)', fontSize: '1.2em', marginBottom: '20px' }}>Change Password</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginBottom: '15px' }}>
                        <div>
                            <label style={labelStyle}>Current Password</label>
                            <input type="password" required value={passwordData.current_password} onChange={e => setPasswordData({...passwordData, current_password: e.target.value})} style={inputStyle} />
                        </div>
                        <div>
                            <label style={labelStyle}>New Password</label>
                            <input type="password" required value={passwordData.new_password} onChange={e => setPasswordData({...passwordData, new_password: e.target.value})} style={inputStyle} />
                        </div>
                    </div>
                    <button type="submit" className="btn" disabled={passwordSaving} style={{ backgroundColor: '#EF4444', color: 'white', padding: '10px 20px', borderRadius: '50px', fontWeight: 'bold' }}>
                        {passwordSaving ? 'Updating...' : 'Update Password'}
                    </button>
                </div>
            </form>
            {cropImageSrc && (
                <ImageCropperModal
                    imageSrc={cropImageSrc}
                    onClose={() => setCropImageSrc(null)}
                    onCropComplete={handleCropComplete}
                />
            )}
        </div>
    );
};

export default RestaurantSettings;
